// Protocolo do Hábito — os marcos do miolo.
//
// O programa tinha começo e fim, e entre o dia 9 e o 66 quase nada. Pior: toda
// celebração era diária-e-idêntica, reativa a falha, ou o dia 67 — nenhuma
// disparada por acerto acumulado. Quem acertava muito via menos coisa acontecer
// do que quem falhava.
//
// Estes marcos são disparados por ACERTO, aparecem uma vez só, e todos usam
// número que já existia. Nenhum promete resultado, nenhum inventa data.
//
// São faixas dentro da página, não modais: interromper quem está indo bem para
// dar parabéns é o tipo de coisa que faz a pessoa fechar o app.

import React from 'react';
import { Icon } from '../icons';

const serif = 'Instrument Serif, Georgia, serif';

const eyebrow: React.CSSProperties = {
  fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600,
};

type Conteudo = { sobre: string; titulo: string; corpo: React.ReactNode; icone: string };

function conteudoDoMarco(chave: string, dados: any): Conteudo | null {
  if (chave === 'seis-semanas') {
    return {
      sobre: 'A dose completa',
      icone: 'target',
      titulo: 'Seis semanas acima de quatro',
      corpo: (<>
        O único estudo que mediu um <b>mínimo</b> para um hábito se estabelecer chegou
        a quatro sessões por semana <b>durante seis semanas</b>. Você acabou de cumprir a
        condição inteira — as duas partes, não só a primeira.
        <br /><br />
        O que vem depois disso o estudo não diz. A mediana de formação fica entre 59 e
        78 dias, e você está no meio dela.
      </>),
    };
  }
  if (chave === 'metade') {
    return {
      sobre: 'Metade do caminho',
      icone: 'flame',
      titulo: 'Dia 34',
      corpo: (<>
        Metade dos 67. Sem promessa junto: o marco é uma contagem, não um resultado
        garantido — a faixa real de formação vai de 18 a 254 dias.
        <br /><br />
        O que dá para dizer com segurança é que a parte mais difícil da curva já ficou
        para trás: as primeiras repetições são as que mais rendem, e você já as fez.
      </>),
    };
  }
  if (chave.startsWith('lembrete-')) {
    const dia = dados?.dia || 22;
    const mapa: Record<number, React.ReactNode> = {
      22: (<>
        A partir de hoje eu te aviso <b>dia sim, dia não</b>. Isso não é abandono, é o
        plano: lembrete aumenta a repetição e <b>atrapalha</b> a automaticidade — o
        gatilho precisa virar a poltrona, não o celular.
        <br /><br />
        No dia 34 eu caio para duas vezes por semana, no 46 para uma, e depois do 67 eu
        saio de cena na hora de ler.
      </>),
      34: (<>
        De hoje em diante são <b>duas vezes por semana</b>. Você deve estar sentindo que
        os ganhos ficaram menores — é o comportamento normal da curva, não perda de
        ritmo. Ela achata de propósito.
      </>),
      46: (<>
        <b>Uma vez por semana</b> a partir de agora. Se você continuar lendo com essa
        frequência de lembrete, é sinal de que o gatilho já mudou de lugar — que era
        exatamente o objetivo.
      </>),
    };
    return {
      sobre: 'A transferência',
      icone: 'bell',
      titulo: dia === 22 ? 'Eu vou te lembrar menos' : dia === 34 ? 'Menos ainda' : 'Quase nada',
      corpo: mapa[dia] || mapa[22],
    };
  }
  if (chave.startsWith('retornos-')) {
    const total = dados?.total || 2;
    return {
      sobre: 'O que o Vale pedia',
      icone: 'flame',
      titulo: total >= 5 ? `${total} voltas` : 'Você sobreviveu ao lapso',
      corpo: (<>
        Você faltou {total} {total === 1 ? 'vez' : 'vezes'} e voltou {total === 1 ? 'uma' : 'todas'}.
        O critério de sucesso desta parte do programa não era não falhar — era
        <b> sobreviver à falha</b>, e é isso que está medido aí.
        <br /><br />
        Entre 53 estratégias testadas com 61.293 pessoas, a que mais funcionou foi
        premiar exatamente quem volta. Você é bom na parte difícil.
      </>),
    };
  }
  return null;
}

export const MarcoDoMiolo: React.FC<{
  chave: string;
  dados?: any;
  onFechar: () => void;
}> = ({ chave, dados, onFechar }) => {
  const c = conteudoDoMarco(chave, dados);
  if (!c) return null;

  return (
    <div style={{ padding: '18px 20px 0' }}>
      <div style={{
        background: '#1A1614', border: '1px solid var(--ember)', borderRadius: 24,
        padding: 22, position: 'relative', overflow: 'hidden',
        boxShadow: '0 18px 40px -18px rgba(199,83,26,0.45)',
        animation: 'habMarco .55s cubic-bezier(.2,.8,.2,1) both',
      }}>
        <style>{`
          @keyframes habMarco { 0% { transform: translateY(10px) scale(.985); opacity: 0 } 100% { transform: none; opacity: 1 } }
          @keyframes habMarcoBrilho { 0%,100% { opacity:.10 } 50% { opacity:.20 } }
        `}</style>
        <div style={{
          position: 'absolute', top: -70, right: -50, width: 190, height: 190, borderRadius: '50%',
          background: 'radial-gradient(circle, var(--ember), transparent 68%)',
          animation: 'habMarcoBrilho 4.5s ease-in-out infinite', pointerEvents: 'none',
        }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 9, position: 'relative' }}>
          <Icon name={c.icone} size={16} color="var(--ember)" />
          <span className="mono" style={{ ...eyebrow, color: 'var(--ember)' }}>{c.sobre}</span>
        </div>

        <div style={{ fontFamily: serif, fontSize: 27, lineHeight: 1.14, color: '#F5F1EA', margin: '11px 0 10px', position: 'relative' }}>
          {c.titulo}
        </div>
        <div style={{ fontSize: 14.5, lineHeight: 1.62, color: 'rgba(245,241,234,0.75)', position: 'relative' }}>
          {c.corpo}
        </div>

        <button onClick={onFechar} style={{
          width: '100%', marginTop: 18, padding: '13px 18px', borderRadius: 999,
          border: '1px solid rgba(245,241,234,0.22)', background: 'transparent',
          color: '#F5F1EA', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', position: 'relative',
        }}>
          Entendi
        </button>
      </div>
    </div>
  );
};

export default MarcoDoMiolo;
